import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { draftPatch } from "./services/crmDraft";
import { etapaLabel } from "./pipelineUtils";

export default function ConfigPlantillasWA({ store }) {
  const [nuevaPlantilla, setNuevaPlantilla] = useState({ nombre: "", mensaje: "", etapa: "", favorita: false });
  const editBase = useRef(null);
  const [error, setError] = useState("");
  const [editPlantillaId, setEditPlantillaId] = useState(null);

  const handleAgregar = () => {
    if (!nuevaPlantilla.nombre.trim() || !nuevaPlantilla.mensaje.trim()) return;
    if (editPlantillaId) {
      try {
        const current = store.config.plantillasWA.find(p => p.id === editPlantillaId);
        const patch = draftPatch(editBase.current, { ...editBase.current, ...nuevaPlantilla }, current);
        store.editarPlantilla(editPlantillaId, patch);
      } catch (e) { setError(e.message); return; }
      setEditPlantillaId(null);
    } else {
      store.agregarPlantilla(nuevaPlantilla);
    }
    setError("");
    setNuevaPlantilla({ nombre: "", mensaje: "", etapa: "", favorita: false });
  };

  const handleEditar = (p) => {
    editBase.current = p; setError("");
    setNuevaPlantilla({ nombre: p.nombre, mensaje: p.mensaje, etapa: p.etapa || "", favorita: p.favorita || false });
    setEditPlantillaId(p.id);
  };

  const handleToggleFavorita = (p) => {
    store.editarPlantilla(p.id, { favorita: !p.favorita });
  };

  return (
    <Box>
      {error && <Alert severity="warning">{error}</Alert>}
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Variables: {"{nombre}"}, {"{curso}"}, {"{negocio}"}, {"{telefono}"}, {"{edad}"}, {"{precio}"}
      </Typography>
      <Stack spacing={1} mb={1}>
        <TextField
          label="Nombre de la plantilla"
          value={nuevaPlantilla.nombre}
          onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, nombre: e.target.value })}
          size="small"
          fullWidth
        />
        <TextField
          label="Mensaje"
          value={nuevaPlantilla.mensaje}
          onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, mensaje: e.target.value })}
          size="small"
          fullWidth
          multiline
          rows={3}
          placeholder="Hola {nombre}, te escribimos de {negocio}. ¿Sigues interesado/a en {curso}?"
        />
        <Stack direction="row" spacing={1}>
          <TextField
            label="Etapa (opcional)"
            value={nuevaPlantilla.etapa}
            onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, etapa: e.target.value })}
            select
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Todas las etapas</MenuItem>
            {store.config.etapasCatalogo.map((e) => (
              <MenuItem key={e.id} value={e.id}>{e.nombre}{e.activa ? "" : " (oculta)"}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={nuevaPlantilla.favorita}
                onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, favorita: e.target.checked })}
                size="small"
              />
            }
            label="Favorita"
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleAgregar}
            disabled={!nuevaPlantilla.nombre.trim() || !nuevaPlantilla.mensaje.trim()}
            startIcon={<AddIcon />}
          >
            {editPlantillaId ? "Guardar" : "Agregar"}
          </Button>
          {editPlantillaId && (
            <Button
              size="small"
              onClick={() => { setEditPlantillaId(null); setNuevaPlantilla({ nombre: "", mensaje: "", etapa: "", favorita: false }); }}
            >
              Cancelar
            </Button>
          )}
        </Stack>
      </Stack>
      {(store.config.plantillasWA || []).length === 0 ? (
        <Typography variant="caption" color="text.secondary">Sin plantillas configuradas</Typography>
      ) : (
        <List dense disablePadding>
          {(store.config.plantillasWA || []).map((p) => (
            <ListItem key={p.id} disableGutters sx={{ alignItems: "flex-start" }}>
              <IconButton size="small" onClick={() => handleToggleFavorita(p)} sx={{ mt: 0.5, mr: 0.5 }}>
                {p.favorita ? <StarIcon sx={{ color: "favorite.main", fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
              </IconButton>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>{p.nombre}</Typography>
                    {p.etapa && <Chip label={etapaLabel(store.config, p.etapa)} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />}
                  </Stack>
                }
                secondary={p.mensaje.length > 100 ? p.mensaje.slice(0, 100) + "..." : p.mensaje}
              />
              <ListItemSecondaryAction>
                <IconButton size="small" onClick={() => handleEditar(p)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" edge="end" onClick={() => store.eliminarPlantilla(p.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
